import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    const slideshow = this.querySelector('slideshow-component');
    slideshow?.addEventListener('slideshow:select', this.#alignThumbnailColumn, { signal });
    window.addEventListener('resize', this.#alignThumbnailColumn, { signal });

    for (const image of this.querySelectorAll('slideshow-component > slideshow-container slideshow-slide img')) {
      image.addEventListener('load', this.#alignThumbnailColumn, { signal });
    }

    const slides = this.querySelector('slideshow-component > slideshow-container slideshow-slides');
    if (slides instanceof Element) {
      this.#thumbnailSlideObserver = new MutationObserver(this.#alignThumbnailColumn);
      this.#thumbnailSlideObserver.observe(slides, {
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-hidden'],
      });
    }

    const frame = this.querySelector('slideshow-container');
    if (frame instanceof Element && 'ResizeObserver' in window) {
      this.#thumbnailAlignObserver = new ResizeObserver(this.#alignThumbnailColumn);
      this.#thumbnailAlignObserver.observe(frame);
    }

    this.#alignThumbnailColumn();
  }

  #controller = new AbortController();

  /** @type {ResizeObserver | undefined} */
  #thumbnailAlignObserver;

  /** @type {MutationObserver | undefined} */
  #thumbnailSlideObserver;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#thumbnailAlignObserver?.disconnect();
    this.#thumbnailSlideObserver?.disconnect();
    this.#controller.abort();
  }

  /**
   * Desktop thumbnails sit on the slide box. Contained photos are centered in that
   * box, so the visible phone starts lower. Push the thumbnail column down to that edge.
   */
  #alignThumbnailColumn = () => {
    const controls = this.querySelector('slideshow-controls[thumbnails][pagination-position="left"]');
    if (!(controls instanceof HTMLElement)) return;

    if (!window.matchMedia('(min-width: 750px)').matches) {
      controls.style.removeProperty('padding-top');
      return;
    }

    const image = this.querySelector(
      'slideshow-component > slideshow-container slideshow-slide[aria-hidden="false"] img'
    );
    const thumbnail = controls.querySelector('.slideshow-controls__thumbnail');
    if (!(image instanceof HTMLImageElement) || !(thumbnail instanceof HTMLElement)) return;
    if (!image.naturalWidth || !image.naturalHeight) return;

    const imageBox = image.getBoundingClientRect();
    const controlsBox = controls.getBoundingClientRect();
    const thumbnailBox = thumbnail.getBoundingClientRect();
    if (imageBox.height < 1 || imageBox.width < 1) return;

    const ratio = image.naturalWidth / image.naturalHeight;
    const renderedHeight = Math.min(imageBox.height, imageBox.width / ratio);
    const visualTop = imageBox.top + (imageBox.height - renderedHeight) / 2;
    const applied = parseFloat(controls.style.paddingTop) || 0;
    const innerOffset = thumbnailBox.top - controlsBox.top - applied;
    const next = Math.max(0, Math.round(visualTop - controlsBox.top - innerOffset));

    if (Math.abs(next - applied) < 1) return;
    controls.style.paddingTop = `${next}px`;
  };

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
